import { translateUrl } from 'next-translate-routes'
import React, { type MouseEventHandler, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Translate } from '@hozana/intl/Translate'
import { useDispatch } from '@hozana/redux/hooks'
import { useRouter } from '@hozana/router'
import { routineToThunk } from '@hozana/routines/functions'
import { useScreen } from '@hozana/screen/useScreen'
import { QA } from '@hozana/tests/constants'
import { toTitleCase } from '@hozana/utils/functions/strings'

import { Button, type TButtonProps } from 'elements/button/Button'
import { Icon } from 'elements/media/Icon'

import { getConnectDetails, updateConnectDetails } from 'general/functions'
import { newRoutes } from 'routes/constants'
import { PAGE } from 'routes/pages'

import { SOCIAL_NAME, getSocialIcon } from 'modules/auth/constants'
import { getSocialAuthUrl } from 'modules/auth/functions'
import { socialLogin } from 'modules/auth/routines'

export type TSocialLoginButtonProps = TButtonProps & {
  socialName: SOCIAL_NAME
  onClick?: MouseEventHandler<HTMLButtonElement>
  onSuccess?: VoidFunction
  onError?: (error: unknown) => void
}

export const SocialLoginButton: TFCC<TSocialLoginButtonProps> = ({
  socialName,
  onClick: propOnClick,
  onSuccess,
  onError,
  ...otherProps
}) => {
  const dispatch = useDispatch()
  const {
    i18n: { language },
  } = useTranslation()
  const { pathname, query } = useRouter()
  const { isMobile } = useScreen()
  const [clicked, setClicked] = useState(false)

  const onClick: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault()

    setClicked(true)
    propOnClick?.(e)

    if (isMobile && !getConnectDetails().redirectUrl) {
      /**
       * Init redirectUrl to current url if not set yet.
       * We cannot do that in SocialAuthPage because we don't have the current pathname anymore
       */
      updateConnectDetails(
        pathname === PAGE.CONNECT
          ? { reloadUrl: newRoutes.prayerSpace(), reload: true }
          : {
              redirectUrl: translateUrl({ pathname, query }, language, { format: 'string' }),
            },
      )
    }
    dispatch(
      routineToThunk(socialLogin, {
        url: getSocialAuthUrl(socialName, language, CONF_KEYS.ENV === 'dev'),
      }),
    )
      .then(onSuccess)
      .catch(onError)
      .finally(() => setClicked(false))
  }

  return (
    <Button
      colors="greyTransparent"
      bg="white"
      shadow
      color={socialName}
      borderColor={socialName}
      hoverBg={socialName}
      hoverColor="white"
      size="bigger"
      onClick={onClick}
      type="button"
      loading={clicked}
      data-testid={QA.AUTH.SOCIAL_CONNECT_BUTTON(socialName)}
      bold={false}
      w="100%"
      {...otherProps}
      m="5px 0 10px"
    >
      <Icon
        vAlign="text-bottom"
        name={getSocialIcon(socialName)}
        size={socialName === SOCIAL_NAME.GOOGLE ? '1em' : '1.4em'}
      />
      &nbsp;&nbsp;
      <Translate values={{ socialName: toTitleCase(socialName) }}>trans:auth:auth.social.continue.with</Translate>
    </Button>
  )
}
